import React from 'react';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { graphql } from 'react-relay';
import classes from './Dashboard.module.scss';
import { DashboardQuery } from './__generated__/DashboardQuery.graphql';
import SurveyList from '../../organisms/SurveyList/SurveyList';
import PollList from '../../organisms/PollList/PollList';
import Streak from '../../organisms/Streak/Streak';
import { SURVEYS, CASHBACK, DEALS } from '../pages';
import useSurveyFilterInputs from '../../../hooks/useSurveyFilterInputs';
import Message from '../../molecules/Message/Message';
import A from '../../atoms/A/A';
import DashboardOfferItem from './DashboardOfferItem';
import ItemList from '../../molecules/ItemList/ItemList';
import ReferFriendTile from './ReferFriendTile';
import Txt from '../../atoms/Txt/Txt';

export interface Props {}

const query = graphql`
  query DashboardQuery($input: FilteredSurveyInput, $first: Int) {
    ...SurveyList_surveys
    polls {
      ...PollList_polls
    }
    gamificationStreak {
      ...Streak_streak
    }
    deals(first: $first) {
      edges {
        node {
          ...DashboardOfferItem_offer
          id
        }
      }
    }
    desktopCashbacks(first: $first) {
      edges {
        node {
          ...DashboardOfferItem_offer
          id
        }
      }
    }
    dashboardMessage {
      ...Message_message
    }
    userData {
      currency {
        currencySymbol
      }
    }
  }
`;

const Dashboard: React.FC<Props> = () => {
  const [filterInputs] = useSurveyFilterInputs();
  const data = useLazyLoadQuery<DashboardQuery>(
    query,
    {
      input: {
        ...filterInputs,
        historicSurveys: false,
      },
      first: 3,
    },
    {
      fetchPolicy: 'store-and-network',
    },
  );
  return (
    <section className={classes.root}>
      {data.dashboardMessage && (
        <Message className={classes.message} messageRef={data.dashboardMessage} />
      )}
      <div className={classes.wrapper}>
        <div className={classes.sectionHeading}>
          <Txt component="h2" className={classes.sectionTitle}>
            Surveys
            <Txt component={A} className={classes.viewAll} to={SURVEYS.path}>
              View all
            </Txt>
          </Txt>
          {data.gamificationStreak && <Streak streakRef={data.gamificationStreak} />}
        </div>
        <SurveyList surveysRef={data} />
      </div>
      <div className={classes.wrapper}>
        <div className={classes.sectionHeading}>
          <Txt component="h2" className={classes.sectionTitle}>
            Cashback
            <Txt component={A} className={classes.viewAll} to={CASHBACK.path}>
              View all
            </Txt>
          </Txt>
        </div>
        {data.desktopCashbacks && (
          <ItemList>
            {data.desktopCashbacks.edges.map((offer) => (
              <li key={offer.node.id}>
                <DashboardOfferItem offerRef={offer.node} />
              </li>
            ))}
          </ItemList>
        )}
      </div>
      <div className={classes.wrapper}>
        <div className={classes.sectionHeading}>
          <Txt component="h2" className={classes.sectionTitle}>
            Deals
            <Txt component={A} className={classes.viewAll} to={DEALS.path}>
              View all
            </Txt>
          </Txt>
        </div>
        {data.deals && (
          <ItemList>
            {data.deals.edges.map((offer) => (
              <li key={offer.node.id}>
                <DashboardOfferItem offerRef={offer.node} />
              </li>
            ))}
          </ItemList>
        )}
      </div>
      <div className={classes.wrapper}>
        <div className={classes.micro}>
          {data.polls && <PollList pollsRef={data.polls} />}
          {data.userData?.currency && (
            <div className={classes.microsection}>
              <Txt component="h2" className={classes.sectionTitle}>
                Refer a Friend
              </Txt>
              <ReferFriendTile currencySymbol={data.userData.currency.currencySymbol} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Dashboard;
